"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { authClient } from "@/lib/auth-client";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { ModalShell } from "@/components/ui/modal-shell";
import { getSafeErrorMessage } from "@/lib/utils";

interface User {
	id: string;
	name: string;
	email: string;
	role?: string;
	banned?: boolean;
	banReason?: string;
	banExpires?: Date;
	createdAt: Date;
	updatedAt: Date;
	MaCB?: string;
}

interface ResetPasswordModalProps {
	isOpen: boolean;
	user: User;
	onClose: () => void;
}

export function ResetPasswordModal({ isOpen, user, onClose }: ResetPasswordModalProps) {
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const generateRandomPassword = () => {
		const length = 12;
		const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
		let password = "";
		for (let i = 0; i < length; i++) {
			password += charset.charAt(Math.floor(Math.random() * charset.length));
		}
		setNewPassword(password);
		setConfirmPassword(password);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");

		if (newPassword !== confirmPassword) {
			setError("Mật khẩu xác nhận không khớp");
			return;
		}

		if (newPassword.length < 6) {
			setError("Mật khẩu phải có ít nhất 6 ký tự");
			return;
		}

		setLoading(true);

		try {
			const { error: resetError } = await authClient.admin.setUserPassword({
				userId: user.id,
				newPassword,
			});

			if (resetError) {
				// `authClient` trả object thường, không phải `Error` — `String(...)` ở đây
				// sẽ hiện "[object Object]" cho người dùng.
				setError(getSafeErrorMessage(resetError));
				return;
			}

			alert(
				`Đã đặt lại mật khẩu cho ${user.name} thành công!\n\nEmail:\n${user.email}\nMật khẩu mới: ${newPassword}\n\nVui lòng sao chép mật khẩu này và gửi cho người dùng.`,
			);
			setNewPassword("");
			setConfirmPassword("");
			onClose();
		} catch (error: any) {
			console.error("Failed to reset password:", error);
			setError(getSafeErrorMessage(error));
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setNewPassword("");
		setConfirmPassword("");
		setError("");
		setShowPassword(false);
		onClose();
	};

	return (
		<ModalShell
			isOpen={isOpen}
			onClose={handleClose}
			title="Đặt lại mật khẩu"
			onSubmit={handleSubmit}
			footer={
				<div className="flex gap-3">
					<Button
						type="button"
						variant="outline"
						onClick={handleClose}
						disabled={loading}
						className="flex-1"
					>
						Hủy
					</Button>
					<Button type="submit" disabled={loading} className="flex-1">
						{loading ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Đang cập nhật...
							</>
						) : (
							"Đặt lại mật khẩu"
						)}
					</Button>
				</div>
			}
		>
			<div className="space-y-4">
				{error && (
					<div className="bg-red-50 border border-red-200 rounded-md p-3 dark:bg-red-950 dark:border-red-800">
						<p className="text-red-800 dark:text-red-300 text-sm">{error}</p>
					</div>
				)}

				<div className="bg-blue-50 border border-blue-200 rounded-md p-3 dark:bg-blue-950 dark:border-blue-800">
					<p className="text-blue-800 dark:text-blue-300 text-sm">
						<strong>Người dùng:</strong> {user.name} ({user.email})
					</p>
				</div>

				<div>
					<label className="block text-sm font-medium text-muted-foreground mb-1">
						Mật khẩu mới <span className="text-red-500 dark:text-red-400">*</span>
					</label>
					<div className="relative">
						<Input
							type={showPassword ? "text" : "password"}
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							placeholder="Nhập mật khẩu mới"
							required
							minLength={6}
							className="pr-10"
						/>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={() => setShowPassword(!showPassword)}
							className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
						>
							{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
						</Button>
					</div>
				</div>

				<div>
					<label className="block text-sm font-medium text-muted-foreground mb-1">
						Xác nhận mật khẩu <span className="text-red-500 dark:text-red-400">*</span>
					</label>
					<Input
						type={showPassword ? "text" : "password"}
						value={confirmPassword}
						onChange={(e) => setConfirmPassword(e.target.value)}
						placeholder="Nhập lại mật khẩu mới"
						required
						minLength={6}
					/>
				</div>

				<div className="flex justify-center">
					<Button type="button" variant="outline" onClick={generateRandomPassword} size="sm">
						Tạo mật khẩu ngẫu nhiên
					</Button>
				</div>

				<div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 dark:bg-yellow-950 dark:border-yellow-800">
					<p className="text-yellow-800 dark:text-yellow-300 text-sm">
						<strong>Lưu ý:</strong> Sau khi đặt lại mật khẩu, hãy sao chép và gửi mật khẩu mới cho
						người dùng một cách an toàn.
					</p>
				</div>
			</div>
		</ModalShell>
	);
}
